import { useLayoutEffect, useState } from 'react';

import { CONTAINER_PADDING_PX } from '@modules/documents/helpers/getDocumentConfig.helper';
import { useScreenWidth } from '@shared/hooks/useScreenWidth.hook';

import { ITEMS_NAV_DRAWER_WIDTH_PX, OPEN_SIDEBAR_PADDING_RIGHT_PX } from '../constants';

// Why the drawer is shut decides whether it may reopen - only a sidebar's own close is ever undone.
enum EDrawerState {
  OPEN = 'open',
  CLOSED_BY_USER = 'closedByUser',
  CLOSED_FOR_SIDEBAR = 'closedForSidebar',
}

type TArgs = {
  hasSidebar: boolean;
  contentWidthPx: number;
  sidebarWidthPx: number;
};

type TReturn = {
  isOpenDrawer: boolean;
  setIsOpenDrawer: (isOpen: boolean) => void;
};

const useNavDrawerWithSidebar = ({ hasSidebar, contentWidthPx, sidebarWidthPx }: TArgs): TReturn => {
  const screenWidth = useScreenWidth();
  const [drawerState, setDrawerState] = useState(EDrawerState.OPEN);

  const availableBesideDrawerPx =
    screenWidth - ITEMS_NAV_DRAWER_WIDTH_PX - OPEN_SIDEBAR_PADDING_RIGHT_PX - contentWidthPx - CONTAINER_PADDING_PX;
  const fitsBesideDrawer = availableBesideDrawerPx >= sidebarWidthPx;

  // Layout effect: the drawer starts closing in the sidebar's first frame so both animate as one move.
  useLayoutEffect(() => {
    if (hasSidebar && !fitsBesideDrawer) {
      setDrawerState((state) => (state === EDrawerState.OPEN ? EDrawerState.CLOSED_FOR_SIDEBAR : state));
    }
    if (!hasSidebar) {
      setDrawerState((state) => (state === EDrawerState.CLOSED_FOR_SIDEBAR ? EDrawerState.OPEN : state));
    }
  }, [hasSidebar, fitsBesideDrawer]);

  return {
    isOpenDrawer: drawerState === EDrawerState.OPEN,
    setIsOpenDrawer: (isOpen) => setDrawerState(isOpen ? EDrawerState.OPEN : EDrawerState.CLOSED_BY_USER),
  };
};

export default useNavDrawerWithSidebar;
